'use client'

import { isServer } from '@tanstack/react-query'
import type { Client } from '@urql/core'
import { addMinutes } from 'date-fns'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { AnyVariables, DocumentInput } from 'urql'
import { undefined } from 'zod'
import { create } from 'zustand'

import pathConfiguration from 'src/configuration/path'
import { NODE_ENV, PUBLIC_APP_URL } from '~/core/constants/env'
import { useGraphQLRequest } from '~/core/hooks/use-graphql'
import useContextGraphQL from '~/core/middleware/use-context-graphql'
import { debounce } from '~/core/ui/utils'
import { LIST_KEYS_TENANT_EXPIRED } from '~/core/utilities/catch-api-error'

import { TENANT_PLANS_STATUS } from '~/lib/features/settings/plans/utilities/enum'
import QueryTenantPlan from '~/lib/graphql/query-tenant-plan'
import { useRouterContext } from '~/lib/next/use-router-context'
import useBoundStore from '~/lib/store'

import type { FeatureName } from './subscription'

type ErrorHandleMap = { [key: string]: () => void }
const catchExt = (graphQLErrors: object[], errorHandleMap: ErrorHandleMap) => {
  const parseErrors = JSON.parse(JSON.stringify((graphQLErrors?.[0] || {}) as object))
  const statusCode =
    parseErrors?.message && typeof parseErrors?.message === 'string'
      ? parseErrors?.extensions?.http_code
      : parseErrors?.message?.extensions?.http_code

  const blockedErrorCode =
    parseErrors?.extensions?.error_code || parseErrors?.message?.extensions?.error_code
  if (
    blockedErrorCode &&
    LIST_KEYS_TENANT_EXPIRED.includes(blockedErrorCode) &&
    [401, 403].includes(statusCode)
  ) {
    if (errorHandleMap) {
      Object.keys(errorHandleMap).forEach((key) => {
        LIST_KEYS_TENANT_EXPIRED.includes(key) && errorHandleMap[key]?.()
      })
    }
  }
}
const createQueryZustand = <Data = any, Variables extends AnyVariables = AnyVariables>(
  document: DocumentInput<Data, Variables>
) => {
  return create<{
    data?: Data | null
    expiredError?: boolean
    fetch: (clientGraphQL: Client, variable?: Variables) => void
    refresh: (clientGraphQL: Client, variable?: Variables) => void
  }>((set, get) => {
    const fetchData = debounce(
      (clientGraphQL: Client, variable: Variables, errorHandleMap: ErrorHandleMap) => {
        set((state) => ({ ...state, loading: true }))
        clientGraphQL
          .query(document, variable)
          .toPromise()
          .then((data) => {
            if (data.error) {
              catchExt(data.error.graphQLErrors, {
                ['EXPIRED_TENANT_PLAN_ERROR']: () => {
                  set((state) => ({ ...state, expiredError: true, data: null }))
                }
              })
            } else {
              set((state) => ({ ...state, data: data.data, loading: false }))
            }
          })
      }
    )
    return {
      fetch: (clientGraphQL: Client, variable?: Variables) =>
        !get().data && fetchData(clientGraphQL, variable),
      refresh: debounce(fetchData, 500)
    }
  })
}

const useSubscriptionData = createQueryZustand(QueryTenantPlan)
const useLocalCache = <T extends object>(data: T | undefined | null, key: string) => {
  const getCacheData = (): { data?: T } =>
    !isServer
      ? JSON.parse(localStorage.getItem(`_cache_${key}`) || '{}')
      : (undefined as { data?: T | undefined })
  const [localCache, setLocalCache] = useState(getCacheData()?.data as T | undefined | null)
  useEffect(() => {
    if (data || data === null) {
      localStorage.setItem(
        `_cache_${key}`,
        JSON.stringify({
          meta: { expiredOn: addMinutes(new Date(), 5) },
          data: data
        })
      )
      setLocalCache(data)
    }
  }, [data])
  return localCache
}
const PAGES_NOT_FETCH_API_4_SUBSCRIPTIONS = [
  pathConfiguration?.authError,
  pathConfiguration?.careers?.list,
  pathConfiguration?.contactUs,
  pathConfiguration?.error404,
  pathConfiguration?.error406,
  pathConfiguration?.errorAccessDenied,
  pathConfiguration?.extension?.list,
  pathConfiguration?.login,
  pathConfiguration?.termsOfUse,
  pathConfiguration?.register,
  pathConfiguration?.onboarding,
  pathConfiguration?.verifyEmail,
  pathConfiguration?.tenantBlocked,
  pathConfiguration?.privacyPolicy,
  pathConfiguration?.selectCompany
]
export const refetchSubscriptionData = useSubscriptionData.getState().refresh
const useSubscriptionPlan = () => {
  const { data: subscriptionData, fetch, refresh, expiredError } = useSubscriptionData()

  const subscriptionDataCache = useLocalCache(subscriptionData, 'subscriptionDataCache')
  const data = useMemo(
    () =>
      subscriptionData?.tenantPlanShow && {
        isBlock: false,
        isTrial: subscriptionData?.tenantPlanShow?.status === TENANT_PLANS_STATUS.trial,
        isExpired:
          subscriptionData?.tenantPlanShow?.tenantPlan &&
          subscriptionData?.tenantPlanShow?.status === TENANT_PLANS_STATUS.expired,
        // subscriptionData?.tenantPlanShow?.tenantPlan &&
        // subscriptionData?.tenantPlanShow?.remainingTrialDays <= 0,
        daysLeft:
          subscriptionData?.tenantPlanShow?.remainingTrialDays > 0
            ? subscriptionData?.tenantPlanShow?.remainingTrialDays
            : 0,
        defaultPlan: subscriptionData?.tenantPlanShow?.tenantPlan?.defaultTrial,
        planFeatures: subscriptionData?.tenantPlanShow?.tenantPlan?.planFeatures
      },
    [subscriptionData]
  )
  const { clientGraphQL } = useContextGraphQL()
  const { user } = useBoundStore()
  const { params, pathnameWithoutLocale } = useRouterContext()
  const previousPath = useRef(pathnameWithoutLocale)
  const [isNavigating, setIsNavigating] = useState(false)
  // ✅ FIX: Track last refresh time to prevent excessive API calls
  const lastRefreshTime = useRef<number>(0)
  const REFRESH_INTERVAL = 5 * 60 * 1000 // 5 phút
  const client = useGraphQLRequest({
    language: user?.language,
    isCareerHub: typeof window !== 'undefined' && pathnameWithoutLocale.search('/careerhub/') > -1
  })
  useEffect(() => {
    const NOT_FETCH_CHUB_DYNAMIC_URL = [
      `${pathConfiguration.careerHub.login(params?.slug as string)}`,
      `${pathConfiguration.careerHub.register(params?.slug as string)}`
    ]

    const isCustomDomain =
      NODE_ENV === 'development'
        ? !['localhost:3000'].includes(window.location.host)
        : !PUBLIC_APP_URL?.includes(window.location.host)

    const isCallApi = () =>
      !PAGES_NOT_FETCH_API_4_SUBSCRIPTIONS.some((url) => pathnameWithoutLocale.startsWith(url)) &&
      !NOT_FETCH_CHUB_DYNAMIC_URL.some((url) => pathnameWithoutLocale.startsWith(url)) &&
      !isCustomDomain &&
      user?.id

    // ✅ FIX: improve use func onfocus
    isCallApi() && fetch(clientGraphQL.query ? clientGraphQL : client, {})

    const onfocus = () => {
      if (isCallApi()) {
        refresh(clientGraphQL.query ? clientGraphQL : client, {})
      }
    }

    if (previousPath.current !== pathnameWithoutLocale) {
      // 🔁 routeChangeStart
      setIsNavigating(true)

      // ✅ FIX: Chỉ refresh khi data chưa có hoặc đã qua 5 phút kể từ lần refresh cuối
      const now = Date.now()
      const shouldRefresh = !subscriptionData || now - lastRefreshTime.current > REFRESH_INTERVAL

      if (shouldRefresh) {
        onfocus()
        lastRefreshTime.current = now
      }

      // ⏳ Simulate delay (for example purpose, you can remove this)
      const timeout = setTimeout(() => {
        // ✅ routeChangeComplete
        setIsNavigating(false)
        onfocus()
        previousPath.current = pathnameWithoutLocale
      }, 300) // Giả lập delay, hoặc bỏ nếu không cần

      return () => clearTimeout(timeout)
    }

    return () => {}
  }, [pathnameWithoutLocale, user, params, clientGraphQL, client, fetch, refresh, subscriptionData])

  return {
    data,
    expiredError,
    subscriptionDataCache,
    planName: useMemo(() => subscriptionData?.tenantPlanShow?.tenantPlan?.name, [data]),
    isLimitReached: useMemo(
      () =>
        subscriptionDataCache?.tenantPlanShow?.planLimitExceeded?.candidate ||
        subscriptionDataCache?.tenantPlanShow?.planLimitExceeded?.job ||
        subscriptionDataCache?.tenantPlanShow?.planLimitExceeded?.tenant_member ||
        subscriptionDataCache?.tenantPlanShow?.planLimitExceeded?.tenant_chub_member ||
        subscriptionDataCache?.tenantPlanShow?.planLimitExceeded?.ai_interviews,
      [subscriptionDataCache]
    ),
    isFeaturePremiumShow: (feature: FeatureName | string) => {
      const planFeature = subscriptionData?.tenantPlanShow?.tenantPlan?.planFeatures?.find(
        (f) => f.key === feature
      )
      return planFeature && !planFeature.enabled
    },
    isFeatureEnabled: (feature: FeatureName | string) => {
      const planFeature = subscriptionData?.tenantPlanShow?.tenantPlan?.planFeatures?.find(
        (f) => f.key === feature
      )
      return planFeature && !!planFeature?.enabled
    },
    isUnLockFeature: (feature: FeatureName | string) => {
      const planFeature = subscriptionData?.tenantPlanShow?.tenantPlan?.planFeatures?.find(
        (f) => f.key === feature
      )
      return planFeature && !!planFeature?.unlocked
    },
    isFeatureFlagOn: (feature: FeatureName | string) => {
      const planFeature = subscriptionData?.tenantPlanShow?.tenantPlan?.planFeatures?.find(
        (f) => f.key === feature
      )
      return planFeature && !!planFeature?.unlocked && !!planFeature?.enabled
    },
    showIconFeatureFromAdminSetting: (feature: FeatureName | string, isOwner: boolean) => {
      const planFeature = subscriptionData?.tenantPlanShow?.tenantPlan?.planFeatures?.find(
        (f) => f.key === feature
      )
      const isUnLock = planFeature && !!planFeature?.unlocked
      return isUnLock || isOwner
    }
  }
}
export default useSubscriptionPlan
